import React, { useEffect, useState } from "react";
import {
  confirmAccountDeletion,
  isAccountDeletionLinkValid,
  type AccountDeletionStatus,
} from "../api/endpoints";
import { VerificationExpiredError } from "../api/config";
import DeletionShell, { formatDaysLeft, formatDeletionDate } from "../components/account/DeletionShell";

type Phase = "checking" | "invalid" | "ready" | "sending" | "scheduled" | "failed";

/** Reads `#token=…` and removes it from the address bar and history entry. */
function takeTokenFromHash(): string {
  if (typeof window === "undefined") return "";
  const token = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token") ?? "";
  if (token) window.history.replaceState(null, "", window.location.pathname);
  return token;
}

/**
 * Landing page of the emailed account-deletion link. Opening it changes
 * nothing — mail scanners and link previewers open URLs — the owner must press
 * the button, which schedules the deletion (it does not erase anything yet).
 * The token lives in the URL fragment so it never reaches a server log.
 */
export default function DeleteAccountConfirmPage(): React.JSX.Element {
  const [token] = useState(takeTokenFromHash);
  const [phase, setPhase] = useState<Phase>(token ? "checking" : "invalid");
  const [status, setStatus] = useState<AccountDeletionStatus | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    isAccountDeletionLinkValid(token)
      .then((ok) => { if (!cancelled) setPhase(ok ? "ready" : "invalid"); })
      .catch(() => { if (!cancelled) setPhase("failed"); });
    return () => { cancelled = true; };
  }, [token]);

  const handleConfirm = async (): Promise<void> => {
    setPhase("sending");
    try {
      setStatus(await confirmAccountDeletion(token));
      setPhase("scheduled");
    } catch (error) {
      setPhase(error instanceof VerificationExpiredError ? "invalid" : "failed");
    }
  };

  return (
    <DeletionShell maxWidth="560px">
      <div className="stack-4">
        {phase === "checking" && <p className="t-small t-mute" role="status" style={{ margin: 0 }}>Checking your link…</p>}

        {phase === "invalid" && (
          <>
            <h1 className="t-h2" style={{ margin: 0 }}>This link no longer works</h1>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              It may have expired (links last 1 hour), already been used, or been copied
              incompletely. Request a new one &mdash; nothing has been scheduled or deleted.
            </p>
            <a className="btn btn--primary" href="/delete-account" style={{ alignSelf: "flex-start" }}>
              Request a new link
            </a>
          </>
        )}

        {(phase === "ready" || phase === "sending") && (
          <>
            <h1 className="t-h2" style={{ margin: 0 }}>Delete your Kivo Skin account?</h1>
            <p className="t-small" style={{ margin: 0 }}>
              Your account, scan photos and results, favorites, preferences and credits will be
              permanently deleted after a waiting period. Until then your account keeps working and
              you can cancel at any time &mdash; we email you a cancel link. A subscription bought
              through the App Store or Google Play is not cancelled by this &mdash; cancel it in your
              store account settings.
            </p>
            <button
              type="button"
              className="btn btn--primary"
              disabled={phase === "sending"}
              onClick={() => { void handleConfirm(); }}
              style={{ alignSelf: "flex-start" }}
            >
              {phase === "sending" ? "Scheduling…" : "Yes, delete my account"}
            </button>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              Didn&apos;t ask for this? Close this page &mdash; nothing happens unless you press the button.
            </p>
          </>
        )}

        {phase === "scheduled" && status && (
          <div className="stack-3" role="status">
            <h1 className="t-h2" style={{ margin: 0 }}>Your account is scheduled for deletion</h1>
            <p className="t-small" style={{ margin: 0 }}>
              It will be permanently deleted on <strong>{formatDeletionDate(status.scheduled_for)}</strong>{" "}
              ({formatDaysLeft(status.days_left)} from now). We have emailed you a link to cancel
              it, in case you change your mind.
            </p>
            <a className="btn btn--primary" href="/" style={{ alignSelf: "flex-start" }}>Back to kivo.skin</a>
          </div>
        )}

        {phase === "failed" && (
          <>
            <h1 className="t-h2" style={{ margin: 0 }}>Something went wrong</h1>
            <p role="alert" className="t-small t-mute" style={{ margin: 0 }}>
              We couldn&apos;t complete this right now. Your link is still valid &mdash; reload the
              page from your email and try again, or write to support@kivo.skin.
            </p>
          </>
        )}
      </div>
    </DeletionShell>
  );
}
